// Legal Acknowledgment API client.
//
// Talks to the auth service's `/auth/legal/*` endpoints (added in PR1-PR3):
//   - GET  /auth/legal/current  → public, returns the published ToS/Privacy
//     versions + sha256 the UI should display + send back on acceptance.
//   - POST /auth/legal/accept    → authenticated, records acceptance and
//     returns the refreshed legal_status.
//
// All routes flow through the gateway; we reuse the shared `authenticatedFetch`
// helper for the POST so CSRF + 401-refresh are handled uniformly. The GET is
// public, so we use `fetch` directly with `skipAuth` (no Authorization, no
// CSRF). The base URL resolution mirrors `api/auth.ts`.

import { authenticatedFetch } from './authenticatedFetch';
import { logger } from '../utils/logger';

// Same conditional as the other modules in this folder - empty in dev so the
// Vite proxy forwards to the gateway, full URL in prod.
const API_BASE = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:8000');

/** A legal document type understood by the backend. Keep in sync with
 *  `legal_service.ALL_DOC_TYPES`. */
export type LegalDocumentType = 'tos' | 'privacy';

/** Response shape for a single document inside `/auth/legal/current.documents`. */
export interface LegalDocumentResponse {
  document_type: LegalDocumentType;
  version?: string | null;
  sha256?: string | null;
  source_url?: string | null;
  effective_at?: string | null;
  published: boolean;
}

/** Response shape for GET /auth/legal/current. */
export interface LegalCurrentResponse {
  documents: Record<LegalDocumentType, LegalDocumentResponse>;
}

/** Per-document snapshot inside `legal_status.current` / `legal_status.accepted`. */
export interface LegalDocumentSnapshot {
  version?: string | null;
  sha256?: string | null;
  accepted_at?: string | null;
}

/** Server-computed gate state. The frontend never recomputes this — it only
 *  renders the gate when `all_ok === false`. */
export interface LegalStatus {
  all_ok: boolean;
  tos_ok: boolean;
  privacy_ok: boolean;
  current: Partial<Record<LegalDocumentType, LegalDocumentSnapshot>>;
  accepted: Partial<Record<LegalDocumentType, LegalDocumentSnapshot>>;
}

/** Single acceptance entry posted to /auth/legal/accept. */
export interface LegalAcceptanceItem {
  document_type: LegalDocumentType;
  version: string;
  sha256: string;
}

/** Response shape for POST /auth/legal/accept. */
export interface LegalAcceptResponse {
  accepted: Record<string, { version: string; sha256: string; accepted_at: string }>;
  legal_status?: LegalStatus | null;
}

/** Thrown when the server rejected the acceptance because the version is
 *  stale (HTTP 409). Callers should refetch `/auth/legal/current` and re-prompt
 *  the user with the new copy. */
export class StaleLegalVersionError extends Error {
  documentType?: string;
  constructor(documentType?: string) {
    super(documentType
      ? `Legal document '${documentType}' is no longer current`
      : 'Legal document is no longer current');
    this.name = 'StaleLegalVersionError';
    this.documentType = documentType;
  }
}

export const legalAPI = {
  /** Fetch the currently-published ToS + Privacy versions. Public endpoint. */
  async getCurrent(): Promise<LegalCurrentResponse> {
    const url = `${API_BASE}/auth/legal/current`;
    logger.log('📜 legalAPI.getCurrent →', url);
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    });
    if (!res.ok) {
      const err = new Error(`HTTP ${res.status}: ${res.statusText}`);
      (err as any).status = res.status;
      throw err;
    }
    return res.json();
  },

  /** Submit the user's acceptance of one or more documents. The request body
   *  must echo back the exact (version, sha256) returned by `getCurrent()`.
   *
   *  Throws `StaleLegalVersionError` on 409 so the UI can refetch and re-prompt. */
  async accept(acceptances: LegalAcceptanceItem[]): Promise<LegalAcceptResponse> {
    try {
      return await authenticatedFetch<LegalAcceptResponse>('/auth/legal/accept', {
        method: 'POST',
        body: JSON.stringify({ acceptances }),
        serviceName: 'Legal',
      });
    } catch (e: any) {
      if (e?.status === 409) {
        const docType = e?.detail?.document_type;
        throw new StaleLegalVersionError(docType);
      }
      throw e;
    }
  },
};
